import { useMsal } from "@azure/msal-react";
import { useEffect, useState, useRef } from "react";
import { clearRedirectSession, setAzurToken } from "../utils";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { b2cPolicies, loginRequest } from "../authConfig";

export const AuthComplete = () => {
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const activeAccount: any = instance.getActiveAccount();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [cookiesUrl, , removeUrlCookie] = useCookies(["redirectData"]);
  const location = useLocation();
  const hasAttemptedRedirect = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAttemptedInteractiveAuth = useRef(false);

  // Check for Azure B2C errors in URL hash
  useEffect(() => {
    if (location.hash) {
      const params = new URLSearchParams(location.hash.substring(1));
      const errorDesc = params.get("error_description");
      const forgotPasswordRequested = errorDesc?.includes("AADB2C90118");

      if (forgotPasswordRequested) {
        hasAttemptedRedirect.current = true;
        instance.loginRedirect({
          authority: b2cPolicies.authorities.forgotPassword.authority,
          scopes: [],
        });
        return;
      }

      if (errorDesc?.includes("AADB2C90091")) {
        hasAttemptedRedirect.current = true;
        removeUrlCookie("redirectData");
        clearRedirectSession();
        navigate("/login", { replace: true });
      }
    }
  }, [instance, location.hash, navigate, removeUrlCookie]);

  // Main authentication flow
  useEffect(() => {
    // Prevent multiple redirects
    if (hasAttemptedRedirect.current || isRedirecting) {
      return;
    }

    const handleAuth = async () => {
      // Wait for MSAL to finish any in-progress operations
      if (inProgress !== "none") {
        return;
      }

      // If no active account after auth is complete, redirect to login
      if (!activeAccount) {
        hasAttemptedRedirect.current = true;
        setIsRedirecting(true);
        removeUrlCookie("redirectData");
        clearRedirectSession();
        navigate("/login", { replace: true });
        return;
      }

      // Try to get token and redirect
      hasAttemptedRedirect.current = true;
      setIsRedirecting(true);

      try {
        let token = activeAccount.idToken;

        // If no token in account, try to acquire it silently
        if (!token) {
          const tokenResponse: any = await instance.acquireTokenSilent({
            account: activeAccount,
            scopes: ["offline_access"],
          });
          token = tokenResponse.idToken;
        }

        if (token) {
          performRedirect(token);
        } else {
          removeUrlCookie("redirectData");
          clearRedirectSession();
          navigate("/login", { replace: true });
        }
      } catch (error: any) {
        // Check if error is interaction_required
        if (
          (error instanceof InteractionRequiredAuthError ||
            error?.errorCode === "interaction_required" ||
            error?.errorMessage?.includes("interaction_required") ||
            (error?.errors && error.errors.includes("interaction_required"))) &&
          !hasAttemptedInteractiveAuth.current
        ) {
          // User interaction required - trigger interactive login
          hasAttemptedInteractiveAuth.current = true;
          try {
            await instance.acquireTokenRedirect({
              account: activeAccount,
              scopes: loginRequest.scopes,
            });
            // acquireTokenRedirect will redirect the page, so we don't need to do anything else
            return;
          } catch (redirectError: any) {
            // If redirect fails, fall back to login page
            console.error(
              "Failed to trigger interactive login:",
              redirectError
            );
            removeUrlCookie("redirectData");
            clearRedirectSession();
            navigate("/login", { replace: true });
          }
        } else {
          // Other errors - redirect to login
          console.error("Token acquisition error:", error);
          removeUrlCookie("redirectData");
          clearRedirectSession();
          navigate("/login", { replace: true });
        }
      }
    };

    handleAuth();
  }, [
    activeAccount,
    inProgress,
    instance,
    navigate,
    removeUrlCookie,
    isRedirecting,
  ]);

  // Fallback timeout - redirect after 7 seconds if still stuck
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (!hasAttemptedRedirect.current) {
        hasAttemptedRedirect.current = true;
        setIsRedirecting(true);
        removeUrlCookie("redirectData");
        clearRedirectSession();

        // Use window.location as ultimate fallback to ensure redirect happens
        try {
          navigate("/login", { replace: true });
        } catch (error) {
          window.location.href = "/login";
        }
      }
    }, 7000); // 7 seconds timeout - aggressive to prevent freezing

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [navigate, removeUrlCookie]);

  // Ultimate safety net - hard redirect after 12 seconds no matter what
  useEffect(() => {
    const ultimateTimeout = setTimeout(() => {
      window.location.href = "/login";
    }, 12000); // 12 seconds - absolute maximum

    return () => clearTimeout(ultimateTimeout);
  }, []);

  const performRedirect = (token: string) => {
    setAzurToken(token);

    // Check if there's a redirect URL from cookies
    if (
      cookiesUrl.redirectData &&
      cookiesUrl.redirectData.redirectUrl &&
      cookiesUrl.redirectData.type
    ) {
      const redirectUrl = cookiesUrl.redirectData.redirectUrl;
      const type = cookiesUrl.redirectData.type;

      removeUrlCookie("redirectData");
      clearRedirectSession();

      if (type === "internal") {
        // Internal redirect - extract path and use navigate for SPA routing
        try {
          const url = new URL(redirectUrl);
          const path = url.pathname + url.search + url.hash;
          navigate(path, { replace: true });
        } catch (error) {
          navigate("/login", { replace: true });
        }
      } else if (type === "resetPass") {
        // Password reset - redirect to external URL
        window.location.href = redirectUrl;
      } else {
        // External app redirect - append token
        const url = redirectUrl + "?token=" + token;
        window.location.href = url;
      }
    } else {
      clearRedirectSession();
      if (token && activeAccount) {
        navigate("/", { replace: true });
      } else {
        navigate("/login", { replace: true });
      }
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <div style={{ fontSize: "24px", fontWeight: "bold" }}>
          {isRedirecting ? "Redirecting..." : "Completing Authentication..."}
        </div>
        <div style={{ fontSize: "16px", color: "#666" }}>
          Please wait while we complete your sign in
        </div>
        {/* Spinner */}
        <div
          style={{
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #3498db",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            animation: "spin 1s linear infinite",
          }}
        ></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </>
  );
};
